import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { and, eq, inArray } from "drizzle-orm";
import z from "zod";

import { db } from "@/db/connection";
import { tournamentDivisionTeams } from "@/db/schema";

const getTournamentDivisionsSchema = z.object({
  ids: z.array(z.number()),
});

export const getTournamentDivisions = createServerFn({
  method: "GET",
})
  .inputValidator(getTournamentDivisionsSchema)
  .handler(async ({ data: { ids } }) => {
    if (ids.length === 0) {
      return [];
    }

    const divisions = await db.query.tournamentDivisions.findMany({
      where: {
        id: {
          in: ids,
        },
        // tournament: {
        // 	visible: true,
        // },
      },
      columns: {
        id: true,
        tournamentId: true,
        divisionId: true,
        name: true,
        gender: true,
        teamSize: true,
        capacity: true,
        waitlistCapacity: true,
        displayGender: true,
        displayDivision: true,
        registrationPrice: true,
        registrationPaused: true,
        status: true,
      },
      with: {
        tournament: {
          columns: {
            id: true,
            name: true,
            date: true,
            registrationOpenAt: true,
          },
          with: {
            venue: {
              columns: {
                id: true,
                name: true,
                city: true,
                slug: true,
              },
            },
          },
        },
        division: true,
        requirements: {
          with: {
            qualifiedDivision: true,
          },
        },
      },
    });

    // Get confirmed and waitlisted team counts for each division
    const teamCounts = await Promise.all(
      divisions.map(async (div) => {
        const [confirmedCount, waitlistedCount] = await Promise.all([
          db.$count(
            tournamentDivisionTeams,
            and(
              eq(tournamentDivisionTeams.tournamentDivisionId, div.id),
              inArray(tournamentDivisionTeams.status, [
                "registered",
                "confirmed",
              ]),
            ),
          ),
          db.$count(
            tournamentDivisionTeams,
            and(
              eq(tournamentDivisionTeams.tournamentDivisionId, div.id),
              eq(tournamentDivisionTeams.status, "waitlisted"),
            ),
          ),
        ]);
        return { id: div.id, confirmedCount, waitlistedCount };
      }),
    );

    return divisions.map((div) => {
      const counts = teamCounts.find((c) => c.id === div.id);
      return {
        ...div,
        confirmedCount: counts?.confirmedCount ?? 0,
        waitlistedCount: counts?.waitlistedCount ?? 0,
      };
    });
  });

export const getTournamentDivisionsQueryOptions = (ids: number[]) =>
  queryOptions({
    queryKey: ["tournamentDivisions", ids],
    queryFn: () => getTournamentDivisions({ data: { ids } }),
  });
